import {
  doc,
  setDoc,
  onSnapshot,
  collection,
  query,
  where,
  limit,
  getDocs,
  serverTimestamp,
  Timestamp,
  getDoc,
  updateDoc,
} from 'firebase/firestore';
import { db, auth } from './firebase';
//  SECURITY: Import device fingerprint service for IP tracking

import { deviceFingerprintService } from './security';

export interface UserPresence {
  userId: string;
  online: boolean;
  lastSeen: Timestamp | null;
  currentTable: string | null;
  username: string;
}

// Stale threshold: users considered offline if lastSeen > 2 minutes ago
const STALE_THRESHOLD_MS = 2 * 60 * 1000;

export const setUserOnline = async (uid: string, username: string): Promise<void> => {
  try {
    //  SECURITY: Get current IP hash for same-IP-at-table detection

    const ipHash = await deviceFingerprintService.getCurrentIpHash().catch(() => null);
    
    const presenceRef = doc(db, 'presence', uid);
    await setDoc(presenceRef, {
      userId: uid,
      online: true,
      lastSeen: serverTimestamp(),
      currentTable: null,
      username,
      //  SECURITY: Include IP hash for multi-account detection

      ...(ipHash && { ipHash }),
    }, { merge: true });
    console.log('[Presence] User set online:', uid, ipHash ? '(IP tracked)' : '');
  } catch (error) {
    console.error('[Presence] Error setting user online:', error);
  }
};

export const setUserOffline = async (uid: string): Promise<void> => {
  try {
    const presenceRef = doc(db, 'presence', uid);
    await updateDoc(presenceRef, {
      online: false,
      lastSeen: serverTimestamp(),
      currentTable: null,
    });
    console.log('[Presence] User set offline:', uid);
  } catch (error) {
    console.error('[Presence] Error setting user offline:', error);
  }
};

export const updateCurrentTable = async (uid: string, tableId: string | null): Promise<void> => {
  try {
    const presenceRef = doc(db, 'presence', uid);
    await updateDoc(presenceRef, {
      currentTable: tableId,
      lastSeen: serverTimestamp(),
    });
  } catch (error) {
    console.error('[Presence] Error updating current table:', error);
  }
};

// Subscribe to active users count - filters out stale users
// Counts OTHER players online (never the caller, never the recorder), so the
// badge always matches what the Find Players "Online" tab lists.
export const subscribeToActiveUsersCount = (callback: (count: number) => void, excludeUid?: string | null): (() => void) => {
  const selfUid = excludeUid === undefined ? (auth.currentUser?.uid || null) : excludeUid;
  const presenceRef = collection(db, 'presence');
  const q = query(presenceRef, where('online', '==', true));
  
  return onSnapshot(q, (snapshot) => {
    const now = Date.now();
    let activeCount = 0;
    
    snapshot.forEach((docSnap) => {
      if (docSnap.id === 'recorder-bot' || (selfUid && docSnap.id === selfUid)) return;
      const data = docSnap.data();
      const lastSeen = data.lastSeen?.toMillis() || 0;
      // Only count users seen within threshold
      if (now - lastSeen < STALE_THRESHOLD_MS) {
        activeCount++;
      }
    });
    
    callback(activeCount);
  }, (error) => {
    console.error('[Presence] Error:', error);
    callback(0);
  });
};

// Subscribe to active users list - filters out stale users
export const subscribeToActiveUsersList = (
  callback: (users: UserPresence[]) => void,
  maxUsers: number = 50
): (() => void) => {
  // Single-field query only: filtering on `online` AND ordering by `lastSeen`
  // needs a composite index, and without one Firestore rejects the query and
  // the list came back empty while the counter (plain filter) said 3 online.
  const presenceRef = collection(db, 'presence');
  const q = query(presenceRef, where('online', '==', true), limit(500));
  
  return onSnapshot(q, (snapshot) => {
    const now = Date.now();
    const users: UserPresence[] = [];
    
    snapshot.forEach((docSnap) => {
      if (docSnap.id === 'recorder-bot') return;
      const data = docSnap.data();
      const lastSeen = data.lastSeen?.toMillis() || 0;
      if (now - lastSeen < STALE_THRESHOLD_MS) {
        users.push({ userId: docSnap.id, ...data } as UserPresence);
      }
    });
    users.sort((a, b) => (b.lastSeen?.toMillis() || 0) - (a.lastSeen?.toMillis() || 0));
    
    callback(users.slice(0, maxUsers));
  }, (error) => { console.error('[Presence] list error:', error); callback([]); });
};

export const getActiveUsersList = async (maxUsers: number = 50): Promise<UserPresence[]> => {
  try {
    // Same single-field query as the live counter; sort here, no composite index
    const presenceRef = collection(db, 'presence');
    const q = query(presenceRef, where('online', '==', true), limit(500));
    const snapshot = await getDocs(q);
    
    const now = Date.now();
    const users: UserPresence[] = [];
    
    snapshot.forEach((docSnap) => {
      if (docSnap.id === 'recorder-bot') return;
      const data = docSnap.data();
      const lastSeen = data.lastSeen?.toMillis() || 0;
      if (now - lastSeen < STALE_THRESHOLD_MS) {
        users.push({ userId: docSnap.id, ...data } as UserPresence);
      }
    });
    users.sort((a, b) => (b.lastSeen?.toMillis() || 0) - (a.lastSeen?.toMillis() || 0));
    
    return users.slice(0, maxUsers);
  } catch (error) {
    console.error('[Presence] Error:', error);
    return [];
  }
};

export const isUserOnline = async (uid: string): Promise<boolean> => {
  try {
    const presenceRef = doc(db, 'presence', uid);
    const snapshot = await getDoc(presenceRef);
    
    if (!snapshot.exists()) return false;
    
    const data = snapshot.data();
    const lastSeen = data.lastSeen?.toMillis() || 0;
    const now = Date.now();
    
    return data.online === true && (now - lastSeen < STALE_THRESHOLD_MS);
  } catch (error) {
    return false;
  }
};

export const updateHeartbeat = async (uid: string): Promise<void> => {
  try {
    const presenceRef = doc(db, 'presence', uid);
    await updateDoc(presenceRef, { 
      lastSeen: serverTimestamp(),
      online: true 
    });
  } catch (error) {
    console.error('[Presence] Error updating heartbeat:', error);
  }
};